import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { auth } from "@/lib/auth";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const START = new Date(2026, 10, 1);
const BLOCKED = new Set(["12-25", "01-01"]);
const DAYS_FR = ["LU", "MA", "ME", "JE", "VE", "SA", "DI"];
const MONTHS_FR = ["JANVIER", "FÉVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOÛT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DÉCEMBRE"];
const MONTHS_EN = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// calendrier partagé admin + 6xo : toutes les réservations (site + manuelles)
export default function CalendarBoard({ provider = "gloom" }) {
    const { lang } = useLang();
    const [month, setMonth] = useState(() => new Date(2026, 10, 1));
    const [bookings, setBookings] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ date: "", name: "", instagram: "", project_type: "MUSIC VIDEO", budget: 450, note: "" });
    const [err, setErr] = useState(null);

    const load = async () => {
        try {
            const r = await auth.fetch(`${API}/api/bookings/all`);
            if (!r.ok) return;
            const d = await r.json();
            setBookings(d.bookings || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const taken = useMemo(() => new Set(bookings.map((b) => b.date)), [bookings]);

    const cells = useMemo(() => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const first = new Date(y, m, 1);
        const lead = (first.getDay() + 6) % 7;
        const days = new Date(y, m + 1, 0).getDate();
        const out = [];
        for (let i = 0; i < lead; i++) out.push(null);
        for (let d = 1; d <= days; d++) out.push(new Date(y, m, d));
        return out;
    }, [month]);

    const stateOf = (d) => {
        if (!d) return "empty";
        const key = iso(d);
        if (d < START) return "off";
        if (BLOCKED.has(key.slice(5))) return "off";
        if (taken.has(key)) return "taken";
        return "free";
    };

    const submit = async () => {
        setErr(null);
        try {
            const r = await auth.fetch(`${API}/api/bookings/manual`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, provider }),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                throw new Error(e.detail || "error");
            }
            await load();
            setAdding(false);
            setForm({ date: "", name: "", instagram: "", project_type: "MUSIC VIDEO", budget: 450, note: "" });
        } catch (e) {
            setErr(String(e.message || e));
        }
    };

    const months = lang === "fr" ? MONTHS_FR : MONTHS_EN;

    return (
        <motion.div
            data-testid="calendar-board"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 bg-white/[0.03] p-6"
        >
            <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-gold">
                    <CalendarDays size={13} /> {lang === "fr" ? "CALENDRIER DES RÉSERVATIONS" : "BOOKING CALENDAR"}
                </p>
                <button
                    data-testid="calendar-add-btn"
                    onClick={() => setAdding(!adding)}
                    className="flex items-center gap-2 border border-violet/60 px-4 py-2 font-mono text-[8px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                >
                    <Plus size={11} /> {lang === "fr" ? "AJOUTER" : "ADD"}
                </button>
            </div>

            {adding && (
                <div className="mt-5 space-y-3 border border-white/10 bg-white/[0.02] p-4">
                    <input data-testid="cal-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none" />
                    <input data-testid="cal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === "fr" ? "Nom du client" : "Client name"} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none placeholder:text-cream/30" />
                    <input data-testid="cal-insta" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="Instagram" className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none placeholder:text-cream/30" />
                    <select data-testid="cal-type" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none">
                        {["MUSIC VIDEO", "COMMERCIAL", "WEDDING", "EVENT", "PHOTO"].map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                    </select>
                    <input data-testid="cal-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} placeholder="Budget CHF" className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none placeholder:text-cream/30" />
                    <input data-testid="cal-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={lang === "fr" ? "Note (optionnel)" : "Note (optional)"} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] text-cream outline-none placeholder:text-cream/30" />
                    {err && <p className="font-mono text-[9px] tracking-[0.2em] text-blood">{err}</p>}
                    <button data-testid="cal-submit" onClick={submit} className="w-full border border-violet/60 py-2.5 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream">
                        {lang === "fr" ? "ENREGISTRER" : "SAVE"}
                    </button>
                </div>
            )}

            <div className="mt-5 flex items-center justify-between">
                <button data-testid="cal-prev" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} disabled={month <= START} className="flex h-9 w-9 items-center justify-center border border-white/15 text-cream/60 transition-colors hover:border-cream hover:text-cream disabled:opacity-20" aria-label="Previous month">
                    <ChevronLeft size={14} />
                </button>
                <p className="font-display text-lg font-extrabold uppercase tracking-wide md:text-xl" data-testid="cal-month">
                    {months[month.getMonth()]} {month.getFullYear()}
                </p>
                <button data-testid="cal-next" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} className="flex h-9 w-9 items-center justify-center border border-white/15 text-cream/60 transition-colors hover:border-cream hover:text-cream" aria-label="Next month">
                    <ChevronRight size={14} />
                </button>
            </div>
            <div className="mt-6 grid grid-cols-7 gap-1">
                {DAYS_FR.map((d) => (
                    <p key={d} className="pb-2 text-center font-mono text-[8px] tracking-[0.2em] text-cream/35">{d}</p>
                ))}
                {cells.map((d, i) => {
                    const st = stateOf(d);
                    const key = d ? iso(d) : `x${i}`;
                    const dayBookings = d ? bookings.filter((b) => b.date === iso(d)) : [];
                    return (
                        <div key={key} className={`aspect-square border p-1 font-mono text-[10px] md:text-[11px] ${st === "empty" ? "border-transparent" : st === "free" ? "border-white/10 text-cream/75" : st === "taken" ? "border-violet/40 bg-violet/10" : "border-transparent text-cream/15"}`}>
                            <p className="text-right">{d ? d.getDate() : ""}</p>
                            {dayBookings.map((b) => (
                                <p key={b.booking_id} className="mt-0.5 truncate text-[7px] text-violet" title={`${b.name} — ${b.project_type} — ${b.provider}`}>
                                    {b.name} {b.provider === "6xo" ? "• 6XO" : ""}
                                </p>
                            ))}
                        </div>
                    );
                })}
            </div>
            <div className="mt-5 flex flex-wrap gap-5 font-mono text-[8px] tracking-[0.25em] text-cream/40">
                <span className="flex items-center gap-2"><span className="h-2 w-2 border border-white/25" /> {lang === "fr" ? "Libre" : "Free"}</span>
                <span className="flex items-center gap-2"><span className="h-2 w-2 bg-violet" /> {lang === "fr" ? "Réservé" : "Booked"}</span>
            </div>
        </motion.div>
    );
}
